// Contact utility for handling WhatsApp, WhatsApp Business, and Telegram links with contextual messages
// Enhanced with Google Analytics event tracking and UTM partner tracking

import { appendPartnerIDToMessage, getPartnerID } from './utmTracker';

const WHATSAPP_NUMBER = '66823204796';
const WHATSAPP_BUSINESS_NUMBER = '66823204796'; // Same number for business account
const TELEGRAM_USERNAME = 'Aidating_love_bot';

export type MessagingApp = 'whatsapp' | 'whatsapp-business' | 'telegram';

interface ContactOptions {
  message?: string;
  preferredMethod?: MessagingApp;
}

/**
 * Track contact events with Google Analytics
 * @param eventName - Name of the GA4 event
 * @param eventParams - Additional parameters for the event
 */
const trackContactEvent = (eventName: string, eventParams: Record<string, any> = {}) => {
  const partnerId = getPartnerID();
  
  if (import.meta.env.MODE === 'production' && typeof (window as any).gtag !== 'undefined') {
    (window as any).gtag('event', eventName, {
      page: window.location.pathname,
      partner_id: partnerId || 'direct',
      ...eventParams
    });
    console.log(`[GA4] Event tracked: ${eventName}`, eventParams);
  }
};

/**
 * Opens WhatsApp with a pre-filled message
 */
export const openWhatsApp = (message: string) => {
  // Append partner ID if available
  const finalMessage = appendPartnerIDToMessage(message);
  const encodedMessage = encodeURIComponent(finalMessage);
  
  // Track WhatsApp contact event
  trackContactEvent('contact_whatsapp', {
    contact_method: 'whatsapp',
    message_preview: message.substring(0, 50),
    has_partner_id: !!getPartnerID()
  });
  
  window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodedMessage}`, '_blank');
};

/**
 * Opens WhatsApp Business with a pre-filled message
 */
export const openWhatsAppBusiness = (message: string) => {
  // Append partner ID if available
  const finalMessage = appendPartnerIDToMessage(message);
  const encodedMessage = encodeURIComponent(finalMessage);
  
  // Track WhatsApp Business contact event
  trackContactEvent('contact_whatsapp_business', {
    contact_method: 'whatsapp_business',
    message_preview: message.substring(0, 50),
    has_partner_id: !!getPartnerID()
  });
  
  window.open(`https://wa.me/${WHATSAPP_BUSINESS_NUMBER}?text=${encodedMessage}`, '_blank');
};

/**
 * Opens Telegram with the AI Dating bot
 */
export const openTelegram = () => {
  // Track Telegram contact event
  trackContactEvent('contact_telegram', {
    contact_method: 'telegram'
  });
  
  window.open(`https://t.me/${TELEGRAM_USERNAME}`, '_blank');
};

/**
 * Opens contact based on selected messaging app
 */
export const openContactWithApp = (messagingApp: MessagingApp, message: string) => {
  switch (messagingApp) {
    case 'whatsapp':
      openWhatsApp(message);
      break;
    case 'whatsapp-business':
      openWhatsAppBusiness(message);
      break;
    case 'telegram':
      openTelegram();
      break;
    default:
      openWhatsApp(message);
  }
};

/**
 * Opens contact based on preference (WhatsApp default, Telegram fallback)
 */
export const openContact = (options: ContactOptions = {}) => {
  const { message = 'Здравствуйте! Я хочу узнать больше о вашем сервисе AI Dating.', preferredMethod } = options;

  // If Telegram is explicitly preferred
  if (preferredMethod === 'telegram') {
    openTelegram();
    return;
  }

  // Default: try WhatsApp first
  // Note: We can't detect if WhatsApp is installed, so we always open WhatsApp
  // User can manually switch to Telegram if needed
  openWhatsApp(message);
};

/**
 * Get contact link for "Book Interview" CTA
 */
export const getInterviewBookingContact = () => {
  return () => {
    // Track interview booking CTA click
    trackContactEvent('cta_click', {
      cta_type: 'interview_booking',
      cta_location: 'hero_section'
    });
    
    openContact({
      message: 'Здравствуйте! Я хочу записаться на интервью с AI Dating.'
    });
  };
};

/**
 * Get contact link for package selection
 */
export const getPackageSelectionContact = (packageName: string) => {
  return () => {
    // Track package selection CTA click
    trackContactEvent('cta_click', {
      cta_type: 'package_selection',
      cta_location: 'pricing_section',
      package_name: packageName
    });
    
    openContact({
      message: `Здравствуйте! Я хочу выбрать пакет "${packageName}" в AI Dating.`
    });
  };
};

/**
 * Get contact link for general inquiry
 */
export const getGeneralInquiryContact = () => {
  return () => {
    // Track general inquiry CTA click
    trackContactEvent('cta_click', {
      cta_type: 'general_inquiry',
      cta_location: 'footer'
    });
    
    openContact({
      message: 'Здравствуйте! Я хочу узнать больше о вашем сервисе AI Dating.'
    });
  };
};

/**
 * Get contact link for partner inquiry (Become a Partner)
 */
export const getBecomePartnerContact = () => {
  return () => {
    // Track become partner CTA click
    trackContactEvent('cta_click', {
      cta_type: 'become_partner',
      cta_location: 'partners_page'
    });
    
    openContact({
      message: 'Хочу стать партнером'
    });
  };
};